import { z } from 'zod';
import { IsoDateTime, Uuid } from './common';

export const BackupKind = z
  .enum(['backup', 'restore_test'])
  .describe(
    '`backup`: database dump + files to every destination. `restore_test`: restore the latest snapshot into a scratch database and folder and verify it.',
  );
export type BackupKind = z.infer<typeof BackupKind>;
export const BackupTrigger = z.enum(['schedule', 'manual']);
export const BackupStatus = z
  .enum(['requested', 'running', 'succeeded', 'partial', 'failed'])
  .describe('`partial`: some destinations failed.');
export type BackupStatus = z.infer<typeof BackupStatus>;
export const BackupErrorCode = z
  .enum(['auth', 'unreachable', 'verify', 'other'])
  .describe(
    '`auth`: the storage refused the key (403); `unreachable`: network or repository not found; `verify`: a restore test found a mismatch.',
  );
export type BackupErrorCode = z.infer<typeof BackupErrorCode>;
export const DestinationKind = z.enum(['local', 's3', 'other']);

/** Per-destination result of a backup run (stored in `details.destinations`). */
export const DestinationResult = z.object({
  name: z.string(),
  /** Env slot (1..3): tells the Admin which `BACKUP_REPO_<n>_*` variables to fix. */
  slot: z.number().int(),
  ok: z.boolean(),
  errorCode: BackupErrorCode.nullable(),
  error: z.string().nullable(),
  snapshotId: z.string().nullable(),
  addedBytes: z.number().int().nullable(),
  durationMs: z.number().int(),
});
export type DestinationResult = z.infer<typeof DestinationResult>;

export const RestoreCheck = z.object({
  destination: z.string(),
  snapshotId: z.string(),
  tables: z.number().int(),
  rows: z.number().int(),
  files: z.number().int(),
  mismatches: z.array(z.string()).describe('Empty when everything matched.'),
});
export type RestoreCheck = z.infer<typeof RestoreCheck>;

export const BackupRunDetails = z.object({
  destinations: z.array(DestinationResult).default([]),
  restore: RestoreCheck.nullable().default(null),
});
export type BackupRunDetails = z.infer<typeof BackupRunDetails>;

export const BackupRun = z
  .object({
    id: Uuid,
    kind: BackupKind,
    trigger: BackupTrigger,
    status: BackupStatus,
    requestedBy: z.string().nullable().describe('Name of who asked for it (null = schedule).'),
    requestedAt: IsoDateTime,
    startedAt: IsoDateTime.nullable(),
    finishedAt: IsoDateTime.nullable(),
    phase: z
      .string()
      .nullable()
      .describe('What it is doing now (pt-BR), e.g. "Copiando arquivos".'),
    percent: z.number().int().min(0).max(100),
    databaseBytes: z.number().int().nullable(),
    filesBytes: z.number().int().nullable(),
    filesCount: z.number().int().nullable(),
    error: z.string().nullable(),
    details: BackupRunDetails,
  })
  .meta({ id: 'BackupRun' });
export type BackupRun = z.infer<typeof BackupRun>;

export const BackupRunList = z.object({
  data: z.array(BackupRun),
  nextCursor: z.string().nullable(),
});
export const BackupRunQuery = z.object({ kind: BackupKind.optional() });
export const CreateBackupRunBody = z
  .object({ kind: BackupKind.default('backup') })
  .meta({ example: { kind: 'backup' } });

export const BackupSchedule = z.object({
  time: z.string().describe('Daily start, HH:MM in `timeZone`.'),
  timeZone: z.string(),
  keepDaily: z.number().int(),
  keepWeekly: z.number().int(),
  keepMonthly: z.number().int(),
  restoreTestDays: z
    .number()
    .int()
    .describe(
      'An automatic restore test runs after the nightly backup when the last one is older than this.',
    ),
});
export type BackupSchedule = z.infer<typeof BackupSchedule>;

/** Hours without a complete backup before the status turns red (spec). */
export const BACKUP_STALE_HOURS = 36;

export const BackupStatusResponse = z
  .object({
    worker: z.object({
      online: z.boolean().describe('The backup service checked in during the last 2 minutes.'),
      seenAt: IsoDateTime.nullable(),
      version: z.string().nullable(),
    }),
    schedule: BackupSchedule.nullable().describe('As reported by the backup service.'),
    lastComplete: z
      .object({
        at: IsoDateTime,
        ageHours: z.number(),
        stale: z.boolean().describe(`Older than ${BACKUP_STALE_HOURS} hours.`),
      })
      .nullable()
      .describe('Last backup that reached every destination.'),
    destinations: z.array(
      z.object({
        name: z.string(),
        kind: DestinationKind,
        slot: z.number().int(),
        lastSuccessAt: IsoDateTime.nullable(),
        lastStatus: z.enum(['ok', 'failed', 'never']),
        consecutiveFailures: z.number().int(),
        errorCode: BackupErrorCode.nullable(),
        error: z.string().nullable(),
      }),
    ),
    sizes: z
      .object({
        databaseBytes: z.number().int(),
        filesBytes: z.number().int(),
        filesCount: z.number().int(),
      })
      .nullable(),
    lastRestoreTest: z
      .object({ at: IsoDateTime, ok: z.boolean(), error: z.string().nullable() })
      .nullable(),
    restoreTestDue: z.boolean(),
    running: BackupRun.nullable().describe('The run requested or in progress, if any.'),
    lastRun: BackupRun.nullable(),
  })
  .meta({ id: 'BackupStatus' });
export type BackupStatusResponse = z.infer<typeof BackupStatusResponse>;
